"use client";

import { useCallback, useMemo, useState } from "react";
import {
  type BriefGroup,
  groupBriefsByCategory,
} from "../../_lib/brief-grouping";
import type { ExploreBriefCard } from "../../types";
import { SectionHeader } from "../section-header";
import { BriefPreviewDialog } from "./brief-preview-dialog";
import { BriefStoriesOverlay } from "./briefs-stories-overlay";
import { PlayAllButton } from "./play-all-button";
import { useBriefsOverlay } from "./use-briefs-overlay";

const MAX_VISIBLE = 5;

// ─── Compact brief row with source + date on right ───────────

function CompactBriefRow({
  brief,
  onClick,
}: {
  brief: ExploreBriefCard;
  onClick: () => void;
}) {
  // Use first tag as source hint, or category
  const sourceLabel =
    brief.tags[0]?.displayName ?? brief.categoryDisplayName ?? "";

  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-bg-highlight)]"
      onClick={onClick}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <p className="min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4] max-sm:line-clamp-2 sm:truncate">
        {brief.title}
      </p>
      {sourceLabel && (
        <span className="hidden shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] sm:inline">
          {sourceLabel}
        </span>
      )}
      <span className="shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
        {brief.timeAgo}
      </span>
    </button>
  );
}

// ─── Category group — NO chevrons, NO collapse ───────────────

function CategoryGroup({
  group,
  onBriefClick,
}: {
  group: BriefGroup;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMore = group.briefs.length > MAX_VISIBLE;
  const remainingCount = group.briefs.length - MAX_VISIBLE;
  const visibleBriefs = isExpanded
    ? group.briefs
    : group.briefs.slice(0, MAX_VISIBLE);

  return (
    <div>
      {/* Category header — no chevron */}
      <div
        className="mb-1.5 flex items-center gap-2 px-4 lg:px-0"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <span className="text-[0.78rem]">{group.categoryEmoji}</span>
        <span className="font-semibold text-[0.78rem] text-[var(--explore-text-primary)]">
          {group.categoryDisplayName}
        </span>
        <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
          ({group.briefs.length})
        </span>
      </div>

      {/* Brief items in card */}
      <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
        <div className="divide-y divide-[var(--explore-border-light)]">
          {visibleBriefs.map((brief) => (
            <CompactBriefRow
              brief={brief}
              key={brief.briefId}
              onClick={() => onBriefClick(brief)}
            />
          ))}
        </div>

        {/* Show more — subtle inline text, no divider */}
        {hasMore && !isExpanded && (
          <button
            className="w-full py-2 text-center text-[0.72rem] text-[var(--explore-text-muted)] transition-colors hover:text-[var(--explore-text-primary)]"
            onClick={() => setIsExpanded(true)}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
            type="button"
          >
            + {remainingCount} more briefs
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────

interface BriefsCompact5SectionProps {
  briefs: ExploreBriefCard[];
  briefsReportDate: string;
  exploreBase: string;
  className?: string;
}

export function BriefsCompact5Section({
  briefs,
  briefsReportDate,
  className,
}: BriefsCompact5SectionProps) {
  const {
    selectedBrief,
    dialogOpen,
    setDialogOpen,
    storiesOpen,
    handleBriefClick,
    handlePlayAll,
    handleCloseStories,
    allViewed,
    handleAllViewed,
  } = useBriefsOverlay();

  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    const group = groups.find(
      (candidate) =>
        candidate.categorySlug ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
    return group?.briefs;
  }, [groups, selectedBrief]);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  if (briefs.length === 0) {
    return null;
  }

  const dateLabel = formatBriefsDate({ reportDate: briefsReportDate });

  return (
    <section className={className}>
      <SectionHeader subtitle={dateLabel} title="Briefs">
        <PlayAllButton
          allViewed={allViewed}
          onPlay={handlePlayAll}
          totalBriefs={briefs.length}
        />
      </SectionHeader>

      <div className="flex flex-col gap-4 px-1 lg:px-0">
        {groups.map((group) => (
          <CategoryGroup
            group={group}
            key={group.categorySlug}
            onBriefClick={handleBriefClick}
          />
        ))}
      </div>

      <BriefPreviewDialog
        brief={selectedBrief}
        categoryBriefs={categoryBriefs}
        onNavigateBrief={handleNavigateBrief}
        onOpenChange={setDialogOpen}
        open={dialogOpen}
      />

      {storiesOpen && (
        <BriefStoriesOverlay
          briefs={briefs}
          onAllViewed={handleAllViewed}
          onClose={handleCloseStories}
          startIndex={0}
        />
      )}
    </section>
  );
}

function formatBriefsDate({ reportDate }: { reportDate: string }): string {
  const [year, month, day] = reportDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}
